// src/components/common/ProtectedRoute.tsx
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSecurityManager } from '../../hooks/useSecurityManager';
import SecurityWarningModal from '../SecurityWarningModal';

interface ProtectedRouteProps {
  element: React.ReactNode;
  requiredRole?: 'admin' | 'farmer' | 'customer';
  requiresSecurityCheck?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  element, 
  requiredRole,
  requiresSecurityCheck = true
}) => {
  const { user, loading } = useAuth();
  
  // Initialiser le gestionnaire de sécurité (seulement ici)
  const {
    hasPermission,
    secureLogout,
    isInactivityWarning,
    timeUntilLogout,
    formatTimeRemaining,
    resetInactivityTimer,
    logSecurityEvent,
    securityState
  } = useSecurityManager({
    enableActivityTracking: requiresSecurityCheck
  });

  // Synchroniser avec localStorage pour SecurityIndicator
  useEffect(() => {
    if (user && requiresSecurityCheck) {
      localStorage.setItem('lastActivity', securityState.lastActivity.toString());
    }
  }, [user, requiresSecurityCheck, securityState.lastActivity]);

  // Log des tentatives d'accès non autorisées
  useEffect(() => {
    if (user && requiredRole && !hasPermission(requiredRole)) {
      logSecurityEvent('UNAUTHORIZED_ROUTE_ACCESS', {
        route: window.location.pathname,
        requiredRole,
        userRoles: {
          is_admin: user.is_admin,
          is_farmer: user.is_farmer,
          is_customer: user.is_customer
        }
      });
    }
  }, [user, requiredRole, hasPermission, logSecurityEvent]);

  // Gestionnaires pour le modal de sécurité
  const handleStayLoggedIn = () => {
    resetInactivityTimer();
    logSecurityEvent('SESSION_EXTENDED_BY_USER');
  };

  const handleLogoutNow = () => {
    secureLogout();
  };

  // États de chargement
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Vérifications de sécurité
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Vérification des rôles
  if (requiredRole && !hasPermission(requiredRole)) {
    // Redirection spécifique selon le rôle de l'utilisateur
    if (user.is_admin) {
      return <Navigate to="/admin" replace />;
    } else if (user.is_farmer) {
      return <Navigate to="/dashboard" replace />;
    } else if (user.is_customer) {
      return <Navigate to="/customer-dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {element}
      
      {/* Modal d'avertissement de déconnexion */}
      {requiresSecurityCheck && (
        <SecurityWarningModal
          isOpen={isInactivityWarning}
          timeRemaining={timeUntilLogout}
          onStayLoggedIn={handleStayLoggedIn}
          onLogoutNow={handleLogoutNow}
          formatTime={formatTimeRemaining}
        />
      )}
    </>
  );
};

export default ProtectedRoute;